import { Dispatch, SetStateAction, useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { mutate } from "swr";
import * as Popover from "@radix-ui/react-popover";
import { TicketIcon } from "@heroicons/react/24/solid";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { BasketSummary } from "@types";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import {
  useInterval,
  usePrevious,
  useThemeContext,
  useWindowSize,
} from "@utils/hooks";
import { formatPrice, getCookieValue, joinClassNames } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import LoadingSpinner from "@components/loadingSpinner";
import { Badge, Button, Card, Heading, Text } from "@components/theming";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  basket: BasketSummary;
  isPended: boolean;
}

export default function Cart({ open, setOpen, basket, isPended }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const theme = useThemeContext();
  const [count, setCount] = useState(basket.Ttl);
  const timer = new Date(count * 1000).toISOString().slice(14, 19);
  const locale = useLocale();
  const basketSubtotal = basket.Groups.reduce(
    (prev, curr) => prev + curr.Subtotal,
    0
  );

  const prevOpen = usePrevious(open).current;
  const cartTranslations = useTranslations("CartPopver");
  const isShopsite = useIsShopsite();
  const windowSize = useWindowSize();
  const isMobile = !!windowSize.width && windowSize.width <= 640;

  useEffect(() => {
    if (!isMobile || !open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, open, setOpen]);

  useInterval(
    () => {
      if (count > 0 && !isPended) {
        setCount(count - 1);
      }
    },
    count > 0 ? 1000 : 0
  );

  useEffect(() => {
    async function handleDelete() {
      const basketKey = getCookieValue("ASP.NET_SessionId");
      const websiteUid = getCookieValue("itk-website-uid");
      const locale = getCookieValue("NEXT_LOCALE");
      if (!basketKey) return;
      const res = await fetch(
        `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}`,
        {
          method: "DELETE",
          headers: {
            "content-language": locale ?? "en",
            "itk-website-uid": websiteUid ?? "",
          },
        }
      );
      if (res.status === 200) {
        await axios.delete("/api/basket", {
          headers: {
            "content-language": locale,
            "itk-website-uid": websiteUid,
          },
        });
        await mutate(`basket/${basketKey}`);
        await mutate(`basket/${basketKey}/summary`);
        await mutate(`basket/${basketKey}/checkout`);
        await mutate(
          (key: any) =>
            Array.isArray(key) && key[0] === `basket/${basketKey}/checkout`
        );
        await mutate(
          (key: any) => Array.isArray(key) && key[0].startsWith("events/"),
          undefined,
          {
            revalidate: true,
          }
        );
        if (pathname.includes("cart") && pathname !== "/cart") {
          router.push("/cart");
        }
      }
    }

    if (count <= 0) {
      handleDelete();
    }
  }, [count, router, pathname]);

  useEffect(() => {
    if (isPended) {
      setOpen(false);
    }
  }, [isPended, setOpen]);

  const renderCartPanelContent = (dark: boolean) => (
    <>
      <Heading
        as="h2"
        size="6"
        weight="bold"
        shopsiteClasses={joinClassNames(
          "text-2xl font-black",
          dark ? "text-white" : "text-black"
        )}
      >
        {cartTranslations("heading")}
      </Heading>
      <div className="flex flex-col gap-2 max-h-[calc(100dvh-328px)] overflow-y-auto">
        {basket.Groups.map((group) => (
          <div key={group.ShowingId}>
            <Heading
              as="h3"
              size="4"
              weight="bold"
              shopsiteClasses={joinClassNames(
                "text-lg font-semibold",
                dark ? "text-white" : "text-black"
              )}
            >
              {group.Event}
            </Heading>
            <Text
              as="p"
              size="2"
              color="gray"
              shopsiteClasses={joinClassNames(
                "text-sm",
                dark ? "text-gray-400" : "text-gray-500"
              )}
            >
              {group.Showing}
            </Text>
            <div className="flex flex-col gap-2 py-2">
              {group.Items.map((item, index) => (
                <Card
                  key={`${item.PriceId}-${item.UniqueId}-${index}`}
                  size="1"
                  sharedClasses="flex items-center justify-between"
                  shopsiteClasses={joinClassNames(
                    "p-2 border rounded-xl",
                    dark
                      ? "bg-indigo-900 border-indigo-600"
                      : "bg-indigo-100 border-indigo-200"
                  )}
                >
                  <Text
                    as="p"
                    size="2"
                    weight="bold"
                    shopsiteClasses={joinClassNames(
                      "font-black text-sm leading-none",
                      dark ? "text-indigo-300" : "text-indigo-800"
                    )}
                  >
                    {item.Name}
                  </Text>
                  <div className="flex flex-col gap-4 justify-between items-end shrink-0">
                    <Text
                      as="p"
                      weight="medium"
                      size="2"
                      shopsiteClasses={joinClassNames(
                        "text-sm font-medium leading-none",
                        dark ? "text-indigo-300" : "text-indigo-800"
                      )}
                    >
                      {item.Cost === 0
                        ? cartTranslations("free")
                        : formatPrice(
                            locale,
                            basket.Currency.CurrencyCode,
                            item.Cost,
                            isShopsite
                          )}
                    </Text>
                    <Badge
                      shopsiteClasses={joinClassNames(
                        "!rounded-lg gap-1 !px-1",
                        dark
                          ? "!bg-indigo-400 !text-indigo-900"
                          : "!bg-indigo-600 !text-indigo-100"
                      )}
                    >
                      <TicketIcon
                        className={joinClassNames(
                          "-rotate-12",
                          isShopsite ? "h-5 w-5" : "h-4 w-4"
                        )}
                      />
                      <Text
                        as="span"
                        size="2"
                        weight="medium"
                        shopsiteClasses="font-medium text-sm"
                      >
                        {cartTranslations("quantity", {
                          quantity: item.Quantity,
                        })}
                      </Text>
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div
        className={joinClassNames(
          "flex items-baseline justify-between mt-4 py-4 border-t",
          isShopsite
            ? dark
              ? "border-gray-800"
              : ""
            : "border-[color:var(--gray-8)]"
        )}
      >
        <Text
          as="p"
          size="4"
          weight="medium"
          shopsiteClasses={joinClassNames(
            "text-lg font-semibold",
            dark ? "text-white" : "text-black"
          )}
        >
          {cartTranslations("subtotal")}
        </Text>
        <Text
          as="p"
          size="7"
          weight="bold"
          shopsiteClasses={joinClassNames(
            "text-3xl font-black",
            dark ? "text-white" : "text-black"
          )}
        >
          {formatPrice(
            locale,
            basket.Currency.CurrencyCode,
            basketSubtotal,
            isShopsite
          )}
        </Text>
      </div>
      <Button
        asChild
        size="3"
        sharedClasses="w-full"
        data-testid="viewcart"
        href="/cart"
        onClick={() => setOpen(false)}
      >
        {cartTranslations("checkout")}
      </Button>
    </>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div
        className={joinClassNames(
          theme && theme.appearance === "light"
            ? "bg-white rounded-[var(--radius-6)]"
            : ""
        )}
      >
        <Popover.Trigger
          data-testid="cartbutton"
          disabled={isPended}
          className={joinClassNames(
            "flex gap-1 px-4 py-2.5 items-center focus:outline-none transition-colors duration-200 ease-in-out disabled:bg-gray-200 bg-white",
            isShopsite
              ? "hover:bg-indigo-50 focus:bg-indigo-50 rounded-full"
              : "rounded-[var(--radius-6)]",
            theme && theme.appearance === "dark"
              ? "hover:bg-[color:var(--accent-a12)] focus:bg-[color:var(--accent-a12)]"
              : theme && theme.appearance === "light"
              ? "hover:bg-[color:var(--accent-a4)] focus:bg-[color:var(--accent-a4)]"
              : ""
          )}
        >
          <div className="relative">
            <ShoppingCartIcon className="h-6 w-6 text-black" />
            {basket.Quantity > 0 ? (
              <Badge
                size="1"
                variant="solid"
                sharedClasses="absolute -right-2"
                whitelabelClasses="-top-2 rounded-[var(--radius-6)]"
                shopsiteClasses="-top-1.5 min-w-[1rem] !px-1 !py-0"
              >
                <Text
                  as="span"
                  size="1"
                  weight="medium"
                  sharedClasses="tabular-nums"
                  shopsiteClasses="text-indigo-900 text-xs"
                >
                  {basket.Quantity}
                </Text>
              </Badge>
            ) : (
              <></>
            )}
          </div>
          {isPended ? (
            <span className="ml-1">
              <LoadingSpinner size="18" />
            </span>
          ) : (
            <span className="tabular-nums text-black">{timer}</span>
          )}
        </Popover.Trigger>
      </div>
      {isMobile ? (
        <AnimatePresence>
          {open && (
            <Popover.Portal
              forceMount
              container={document.getElementById(
                isShopsite ? "shopsite-root-element" : "whitelabel-root-element"
              )}
            >
              <div className="contents">
                <motion.div
                  key="cart-mobile-backdrop"
                  className="fixed inset-0 z-40 bg-black/50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setOpen(false)}
                  aria-hidden="true"
                />
                <motion.div
                  key="cart-mobile-panel"
                  data-testid="cartcontent"
                  role="dialog"
                  aria-modal="true"
                  aria-label={cartTranslations("heading")}
                  className="fixed inset-x-0 top-[5.5rem] z-[49] w-full overflow-hidden bg-black rounded-b-2xl shadow-lg outline-none"
                  initial={prevOpen === false ? "hidden" : "show"}
                  animate="show"
                  exit="hidden"
                  variants={{
                    hidden: { height: 0, transition: { duration: 0.125 } },
                    show: { height: "auto" },
                  }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="flex flex-col gap-2 max-h-[calc(100dvh-5.5rem)] overflow-y-auto p-4">
                    {renderCartPanelContent(true)}
                  </div>
                </motion.div>
              </div>
            </Popover.Portal>
          )}
        </AnimatePresence>
      ) : (
        <AnimatePresence>
          {open && (
            <Popover.Portal
              forceMount
              container={document.getElementById(
                isShopsite ? "shopsite-root-element" : "whitelabel-root-element"
              )}
            >
              <Popover.Content
                data-testid="cartcontent"
                className="relative z-50"
                sideOffset={5}
                align="end"
                alignOffset={-64}
                forceMount
              >
                <Card
                  asChild
                  variant="classic"
                  size="3"
                  shopsiteClasses="relative rounded-2xl m-2 p-4 bg-white max-h-[calc(100dvh-120px)] w-96 shadow ring-1 ring-inset ring-[#E5E7EB]"
                  whitelabelClasses="w-96 m-2"
                >
                  <motion.div
                    initial={prevOpen === false ? "hidden" : "show"}
                    animate="show"
                    exit="hidden"
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: "-1rem",
                        backdropFilter: isShopsite
                          ? ""
                          : "blur(64px) opacity(0)",
                        transition: { duration: 0.125 },
                      },
                      show: {
                        opacity: 1,
                        backdropFilter: isShopsite
                          ? ""
                          : "blur(64px) opacity(1)",
                        y: 0,
                      },
                    }}
                    transition={{
                      duration: 0.25,
                    }}
                  >
                    {renderCartPanelContent(false)}
                  </motion.div>
                </Card>
              </Popover.Content>
            </Popover.Portal>
          )}
        </AnimatePresence>
      )}
    </Popover.Root>
  );
}
